import React from "react";
import { useNavigate, useNavigationType } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import useGoTo from "./useGoTo";
import useToast from "./useToast";
import {
  commitOrder,
  cancelOrder,
  incrementOrderQtt,
  decrementOrderQtt,
} from "../ducks/shoppingCartSlice";
import { selectLayout } from "../ducks/shopSelectors";
import { trackAddToCart } from "../ducks/tracking";
import { unansweredRequiredQuestions } from "../utils/questions";

/**
 * O item que está sendo montado na tela de produto: quantidade, perguntas
 * pendentes e o "adicionar à sacola", com as mesmas regras no celular e no
 * totem. Quem desenha o rodapé é o FloatingProductOrderFooter(Totem).
 *
 * `ready` fica falso até o custo do item ser calculado (ou se o item ou o
 * produto sumirem do estado); antes disso não há o que mostrar.
 */
export default function useProductOrder({
  orderUuid,
  orderUuidEdit,
  isOrderVisible,
  returnPath,
}) {
  const dispatch = useDispatch();
  const { toastSuccess } = useToast();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const goTo = useGoTo();
  const { user18Older, kilogramStep } = useSelector((state) => state.shop);
  const layout = useSelector(selectLayout);
  const usesWizard = layout.product === "totem";
  const productOrder = useSelector((state) =>
    state.shoppingCart.productOrders.find((o) => o.uuid === orderUuid),
  );
  const { qtt, productId } = productOrder || {};
  const product = useSelector((state) =>
    state.products.items.find((p) => p.id === productId),
  );
  const cachedCost = useSelector(
    (state) => state.shoppingCart.cachedCostPerOrder[orderUuid],
  );
  const questions = useSelector(
    (state) => state.products.questionsByProduct[productId],
  );
  const answers = useSelector(
    (state) => state.shoppingCart.answersByOrder[orderUuid],
  );
  const [show18Years, setShow18Years] = React.useState(false);
  const [showWizard, setShowWizard] = React.useState(false);

  // Sem o item ou o produto não há o que desenhar. Lançar aqui derruba o app
  // inteiro: o UnknownErrorGuard troca tudo pela tela de "Ops...".
  if (!cachedCost || !productOrder || !product) {
    return { ready: false };
  }

  const unansweredRequireds = unansweredRequiredQuestions(questions, answers);
  const { isKilogram, isAlcoholic } = product;
  const qttMinimum = isKilogram ? kilogramStep : 1;

  const boundPayload = { uuid: orderUuid, isKilogram, kilogramStep };
  const increment = () => dispatch(incrementOrderQtt(boundPayload));
  const decrement = () => dispatch(decrementOrderQtt(boundPayload));

  const commitToCart = () => {
    dispatch(commitOrder(boundPayload));
    if (orderUuidEdit) {
      dispatch(cancelOrder({ uuid: orderUuidEdit }));
      toastSuccess("Produto alterado!");
    } else {
      toastSuccess("Produto adicionado!");
      // editar é cancelar e adicionar de novo: não é uma adição para o funil
      dispatch(trackAddToCart(orderUuid));
    }

    if (navigationType === "PUSH") {
      navigate(-1);
    } else {
      goTo(returnPath);
    }
  };

  // No totem, com perguntas, o primeiro toque abre o wizard e o último (dentro
  // dele) adiciona.
  const addToCart = () => {
    if (
      questions.length > 0 &&
      (unansweredRequireds.length > 0 || (usesWizard && !showWizard))
    ) {
      setShowWizard(true);
      return;
    }

    // No totem, tablet e POS o aparelho é compartilhado: a resposta de um
    // cliente não vale para o próximo, então pergunta sempre.
    if (isAlcoholic && (layout.sharedDevice || !user18Older)) {
      setShow18Years(true);
      return;
    }

    commitToCart();
  };

  const disabled = !isOrderVisible || product.disabled;

  const buttonLabel = !isOrderVisible
    ? "Carregando..."
    : product.disabled
      ? "Produto em falta"
      : orderUuidEdit
        ? "Alterar"
        : "Adicionar";

  return {
    ready: true,
    orderUuid,
    product,
    qtt,
    qttMinimum,
    isKilogram,
    cachedCost,
    questions,
    answers,
    unansweredRequireds,
    increment,
    decrement,
    addToCart,
    commitToCart,
    disabled,
    buttonLabel,
    showWizard,
    openWizard: () => setShowWizard(true),
    closeWizard: () => setShowWizard(false),
    show18Years,
    setShow18Years,
  };
}
